import React from 'react';
import { agGridDataFormatter, shouldRenderAsHtml } from './helpers/TableColumnFormatting';
import { getFieldName } from './helpers/getFieldName';
import { ICellRendererParams } from '@ag-grid-community/core';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import { CustomLinkRenderer } from './CustomLinkRenderer';
import { defaultLinkedAction, defaultTableActionsContext } from './helpers/tableActionsHelper';
import { TableActionsContextField } from './TableActionsButton/types';
import { CustomLabels } from 'common/visualizations/vif';

export interface IValueRendererProps extends ICellRendererParams<any, any, Partial<CustomAgGridContext>> {
  domain: string;
  datasetUid: string;
  wrapWithDivElement: boolean;
  forExport?: boolean;
  customLabels?: CustomLabels;
}

export const ValueRenderer = (params: IValueRendererProps) => {
  const ctx = params.context;
  const columnMetadata = ctx.columnMetadata ?? [];
  const columnFormats = ctx.columnFormats ?? {};
  const nonStandardAggregations = ctx.nonStandardAggregations ?? [];
  const linkedAction = ctx.linkedAction ?? defaultLinkedAction;
  const tableActionsContext = ctx.tableActionsContext ?? defaultTableActionsContext;
  const fieldName = getFieldName(params);

  const renderTextAsHtml = (text: string) => {
    return <div dangerouslySetInnerHTML={{ __html: text }}></div>;
  };

  const formattedValue = agGridDataFormatter(
    params.domain,
    params.datasetUid,
    columnMetadata,
    nonStandardAggregations,
    params,
    columnFormats,
    false,
    false,
    params.customLabels,
  );

  // Currently, we do not need to export values enclosed in div elements in the Excel export.
  if (params.forExport) {
    return formattedValue;
  }

  if (linkedAction?.selectedExistingColumn && fieldName === linkedAction?.selectedExistingColumn) {
    const { mappedColumns } = tableActionsContext;
    const selectedColumns = mappedColumns?.map(
      (mappedColumn: TableActionsContextField) => mappedColumn.selectedColumn,
    );

    const mappedColumnsWithValue = mappedColumns
      ? mappedColumns.map((contextField: TableActionsContextField) => {
          const value = params.data[contextField.selectedColumn];
          return {
            ...contextField,
            value,
          };
        })
      : null;

    const selectedActionForExistingColumn = linkedAction.selectedActionForExistingColumn;

    // Don't render link if no primary key value - just show plain text
    if (!selectedColumns || selectedColumns.length === 0) {
      return formattedValue;
    }

    return (
      <CustomLinkRenderer
        formattedValue={formattedValue}
        tableActionsContext={{ ...tableActionsContext, mappedColumns: mappedColumnsWithValue }}
        selectedActionForExistingColumn={selectedActionForExistingColumn}
      />
    );
  }

  return params.wrapWithDivElement || shouldRenderAsHtml(params.node, columnMetadata)
    ? renderTextAsHtml(formattedValue)
    : formattedValue;
};
